// The signed-in staff shell: sidebar on laptops, header and tab bar on phones.
// Loaded only for staff, so candidate and client links stay light.
import { staffHelpKey } from "@crm/help/pages";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Suspense } from "react";
import { Connie } from "../components/connie.tsx";
import { Header, PlatformBanner, SimpleHeader } from "../components/header.tsx";
import { HelpFirstVisit, HelpHost } from "../components/help.tsx";
import { Nav } from "../components/nav.tsx";
import { QuickFind } from "../components/quick-find.tsx";
import { Sidebar } from "../components/sidebar.tsx";
import { InShell } from "../components/ui.tsx";
import { meQuery } from "../lib/api.ts";
import type { HelpSource } from "../lib/help.ts";

const HELP: HelpSource = { base: "/help", staff: true };

/** The help key for the page on screen (from the deepest matched route). */
function useHelpKey() {
	const routeId = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId });
	return staffHelpKey(routeId?.replace(/^\/staff(\/tenant)?/, "") || "/");
}

export function StaffLayout() {
	const { data: me } = useSuspenseQuery(meQuery);
	const navigate = useNavigate();
	const helpKey = useHelpKey();
	const help = <HelpHost source={HELP} pageKey={helpKey} go={(path) => void navigate({ to: path as "/" })} />;
	const firstVisit = (
		<div className="mx-auto w-full max-w-[64rem] px-4 pt-4 empty:hidden lg:max-w-[96rem] lg:px-8">
			<Suspense fallback={null}>
				<HelpFirstVisit key={helpKey} source={HELP} pageKey={helpKey} />
			</Suspense>
		</div>
	);
	if (me?.activeAgencyId) {
		// Laptops: sidebar beside the page. Phones: header on top, tab bar at the bottom.
		return (
			<div className="lg:flex">
				<Sidebar me={me} />
				<div className="min-w-0 flex-1">
					<PlatformBanner me={me} />
					<Header me={me} />
					<Nav me={me} />
					{/* Room at the bottom so the tab bar and the Feedback button never cover the last thing on a page. */}
					<div className="pb-40 sm:pb-24">
						{firstVisit}
						<InShell value={true}>
							<Outlet />
						</InShell>
					</div>
				</div>
				<Connie />
				<QuickFind me={me} />
				{help}
			</div>
		);
	}
	return (
		<>
			{me ? <SimpleHeader me={me} /> : null}
			<div className="pb-24">
				{firstVisit}
				<Outlet />
			</div>
			{help}
		</>
	);
}
